// src/utils/imagekitUpload.js
// Direct ImageKit Upload (Client-side) - for Development
// WARNING: Private key exposed. Use backend in production.

const IMAGEKIT_URL_ENDPOINT = process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT;
const IMAGEKIT_PUBLIC_KEY = process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY;
const IMAGEKIT_PRIVATE_KEY = process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY;

/**
 * Generate ImageKit authentication parameters (client-side)
 * Note: Ye insecure hai, sirf development ke liye.
 */
const getAuthenticationParameters = () => {
  const token = Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
  const expire = Math.floor(Date.now() / 1000) + 1800; // 30 minutes

  // Generate signature using HMAC-SHA1
  // Note: Ye sirf simple demonstration ke liye hai.
  // Production mein backend se signature generate karein.
  const signature = generateSignature(token, expire, IMAGEKIT_PRIVATE_KEY);

  return { token, expire, signature };
};

/**
 * Simple HMAC-SHA1 signature generator (using Web Crypto API)
 */
const generateSignature = (token, expire, privateKey) => {
  // Fallback: ImageKit accepts signature generated with "token + expire + privateKey" 
  // but for HMAC-SHA1 we need crypto library.
  // For simplicity, we'll use a simple SHA1 hash (may not work in all cases).
  // Better solution: Use a small crypto-js library.
  
  // Filhal hum signature blank bhejenge aur upload API key ke saath kaam karega.
  // Ye trick testing ke liye kaam karti hai agar ImageKit ki settings mein 
  // "Unsigned uploads" enable ho.
  return '';
};

/**
 * Upload file to ImageKit
 * @param {File} file - The file to upload
 * @param {string} folder - Folder path in ImageKit (e.g., 'products', 'banners')
 * @returns {Promise<string>} - Public URL of uploaded image
 */
export const uploadToImageKit = async (file, folder = 'uploads') => {
  if (!file) throw new Error('No file provided');

  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }

  // Validate size (max 5 MB)
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('File size must be less than 5 MB');
  }

  const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', fileName);
  formData.append('publicKey', IMAGEKIT_PUBLIC_KEY);
  formData.append('folder', `/${folder}/`);
  formData.append('useUniqueFileName', 'true');

  try {
    const response = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
      method: 'POST',
      headers: {
        'Authorization': 'Basic ' + btoa(IMAGEKIT_PRIVATE_KEY + ':'),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('ImageKit upload error:', errorData);
      throw new Error(errorData.message || 'Upload failed');
    }

    const data = await response.json();
    return data.url; // Public URL

  } catch (error) {
    console.error('ImageKit upload failed:', error);
    throw error;
  }
};

/**
 * Delete image from ImageKit (optional)
 */
export const deleteFromImageKit = async (fileId) => {
  try {
    const response = await fetch(`https://api.imagekit.io/v1/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': 'Basic ' + btoa(IMAGEKIT_PRIVATE_KEY + ':'),
      },
    });

    if (!response.ok) throw new Error('Delete failed');
    return true;
  } catch (error) {
    console.error('ImageKit delete error:', error);
    return false;
  }
};

export default uploadToImageKit;